import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  clearActivityLog,
  deleteActivityEntry,
  fetchActivityLog,
  loadActivityLog,
  subscribeActivityLog,
} from '../services/activityLog';
import { isSupabaseEnabled } from '../services/supabaseClient';

const ActivityLogContext = createContext(null);

export function ActivityLogProvider({ children }) {
  const [logs, setLogs] = useState(loadActivityLog);
  const [isLoading, setIsLoading] = useState(isSupabaseEnabled());

  const refreshLogs = useCallback(async () => {
    if (isSupabaseEnabled()) {
      const entries = await fetchActivityLog();
      setLogs(entries);
      return entries;
    }

    const entries = loadActivityLog();
    setLogs(entries);
    return entries;
  }, []);

  const removeEntry = useCallback(async (id) => {
    const result = await deleteActivityEntry(id);
    if (result.ok) {
      await refreshLogs();
    }
    return result;
  }, [refreshLogs]);

  const clearAll = useCallback(async () => {
    await clearActivityLog();
    setLogs([]);
    await refreshLogs();
  }, [refreshLogs]);

  useEffect(() => {
    if (!isSupabaseEnabled()) return undefined;

    let cancelled = false;

    fetchActivityLog().then((entries) => {
      if (!cancelled) {
        setLogs(entries);
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return subscribeActivityLog(() => {
      setLogs(loadActivityLog());
    });
  }, []);

  return (
    <ActivityLogContext.Provider value={{ logs, isLoading, refreshLogs, removeEntry, clearAll }}>
      {children}
    </ActivityLogContext.Provider>
  );
}

export function useActivityLog() {
  const context = useContext(ActivityLogContext);
  if (!context) {
    throw new Error('useActivityLog must be used within ActivityLogProvider');
  }
  return context;
}
